// ── Provider Config ─────────────────────────────────────────────────────────

export type ProviderName =
  | "stripe"
  | "manual"
  | "ifthenpay"
  | "paypal"
  | "adyen"
  | "easypay"
  | "eupago"
  | "sequra";

export type PaymentMethodType =
  | "credit_card"
  | "mbway"
  | "multibanco"
  | "bank_transfer"
  | "apple_pay"
  | "google_pay"
  | "cheque"
  | "floa"
  | "sequra";

export const PROVIDER_NAMES: ProviderName[] = [
  "stripe",
  "manual",
  "ifthenpay",
  "paypal",
  "adyen",
  "easypay",
  "eupago",
  "sequra",
];

export const PAYMENT_METHOD_TYPES: PaymentMethodType[] = [
  "credit_card",
  "mbway",
  "multibanco",
  "bank_transfer",
  "apple_pay",
  "google_pay",
  "cheque",
  "floa",
  "sequra",
];

export const PROVIDER_LABELS: Record<ProviderName, string> = {
  stripe: "Stripe",
  manual: "Transferencia Bancaria",
  ifthenpay: "IfthenPay",
  paypal: "PayPal",
  adyen: "Adyen",
  easypay: "Easypay",
  eupago: "EuPago",
  sequra: "SeQura",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethodType, string> = {
  credit_card: "Cartao de Credito",
  mbway: "MB WAY",
  multibanco: "Multibanco",
  bank_transfer: "Transferencia Bancaria",
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  cheque: "Cheque",
  floa: "FLOA (3x)",
  sequra: "SeQura",
};

export interface TenantProviderConfig {
  id: string;
  tenant_id: string;
  provider_name: ProviderName;
  is_enabled: boolean;
  publishable_key?: string;
  environment: "live" | "test";
  api_key_masked?: string;
  manual_bank_name?: string;
  manual_iban?: string;
  manual_bic?: string;
  manual_account_holder?: string;
  manual_payable_to?: string;
  manual_mailing_address?: string;
  manual_ttl_hours?: number;
  created_at: string;
  updated_at: string;
}

export interface UpsertProviderConfigRequest {
  provider_name: string;
  is_enabled: boolean;
  api_key?: string;
  webhook_secret?: string;
  publishable_key?: string;
  environment?: string;
  manual_bank_name?: string;
  manual_iban?: string;
  manual_bic?: string;
  manual_account_holder?: string;
  manual_payable_to?: string;
  manual_mailing_address?: string;
  manual_ttl_hours?: number;
}

// ── Route Config (Payment Methods) ─────────────────────────────────────────

export interface ProviderRouteConfig {
  id: string;
  tenant_id: string;
  payment_method_type: PaymentMethodType;
  provider_name: ProviderName;
  is_enabled: boolean;
  priority: number;
  display_name?: string;
  description?: string;
  icon?: string;
  availability_rules?: AvailabilityRule[];
  created_at: string;
  updated_at: string;
}

export interface AvailabilityRule {
  type: "authenticated" | "nif" | "nif_prefix";
  kind?: "company" | "individual";
  prefixes?: string[];
}

export interface UpsertRouteRequest {
  payment_method_type: string;
  provider_name: string;
  is_enabled: boolean;
  priority?: number;
  display_name?: string;
  description?: string;
  icon?: string;
  availability_rules?: AvailabilityRule[];
}

/**
 * Builds the availability_rules array from the editor controls.
 * Pure function shared by the payment-method form so the encoding of
 * operator intent → rules is testable and identical everywhere.
 */
export function buildAvailabilityRules(
  requireAuth: boolean,
  nifRequirement: string,
  nifPrefixes: string,
): AvailabilityRule[] {
  const rules: AvailabilityRule[] = [];
  if (requireAuth) rules.push({ type: "authenticated" });
  if (nifRequirement === "any") rules.push({ type: "nif" });
  else if (nifRequirement === "company" || nifRequirement === "individual")
    rules.push({ type: "nif", kind: nifRequirement });
  const prefixes = nifPrefixes
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (prefixes.length > 0) rules.push({ type: "nif_prefix", prefixes });
  return rules;
}
